// 제자리 달리기·걷기 클립의 '발이 땅을 미는 속도' 측정(로드 때 한 번) + 재생 배속 계산(무빙 다듬기 2026-11-03).
// 위치는 게임이 정하고 클립은 제자리라, 재생 배속 = 실제 이동 속도 / 클립 지면 속도여야 발이 미끄러지지 않는다.
// 용병(mercs.ts)은 예전 고정 1.05배라 느리게 따라올 때 발이 미끄러지고 빠를 때 헛걸음을 했다.
import * as THREE from 'three/webgpu';

/**
 * clip을 mixer로 samples 프레임 돌려, 매 프레임 가장 낮은 발(디딤발)의 골반 기준 수평 이동 속도 중앙값(모델 공간 m/s)을 낸다.
 * 디딤발은 클립 안에서 몸 뒤로 쓸리므로 그 속도가 '클립이 1배속일 때 걸맞은 이동 속도'다. 실패하면 0.
 * 로드 때만 부른다(객체를 만든다).
 */
export function measureClipGroundSpeed(model: THREE.Object3D, mixer: THREE.AnimationMixer, clip: THREE.AnimationClip, samples = 48): number {
  const bones: THREE.Object3D[] = [];
  let hips: THREE.Object3D | null = null;
  model.traverse((o) => {
    if (/(Left|Right)Foot$/.test(o.name)) bones.push(o);
    else if (!hips && /Hips$/.test(o.name)) hips = o;
  });
  if (bones.length < 2 || !hips) return 0;
  const action = mixer.clipAction(clip);
  action.reset(); action.setLoop(THREE.LoopRepeat, Infinity); action.play();
  const h = new THREE.Vector3(), v = new THREE.Vector3();
  const cur = bones.map(() => new THREE.Vector3()), prev = bones.map(() => new THREE.Vector3());
  const speeds: number[] = [];
  const dt = clip.duration / samples;
  const inv = new THREE.Matrix4();
  for (let f = 0; f <= samples; f++) {
    action.time = f * dt; mixer.update(0);
    model.updateMatrixWorld(true);
    inv.copy(model.matrixWorld).invert();
    (hips as THREE.Object3D).getWorldPosition(h).applyMatrix4(inv);
    let low = 0;
    for (let j = 0; j < bones.length; j++) {
      bones[j].getWorldPosition(v).applyMatrix4(inv);
      cur[j].set(v.x - h.x, v.y, v.z - h.z);
      if (cur[j].y < cur[low].y) low = j;
    }
    // 디딤발(가장 낮은 발)이 앞 프레임에서도 가장 낮았을 때만 센다(발 바꿈 순간 제외)
    if (f > 0) {
      let prevLow = 0;
      for (let j = 1; j < bones.length; j++) if (prev[j].y < prev[prevLow].y) prevLow = j;
      if (prevLow === low) speeds.push(Math.hypot(cur[low].x - prev[low].x, cur[low].z - prev[low].z) / dt);
    }
    for (let j = 0; j < bones.length; j++) prev[j].copy(cur[j]);
  }
  action.stop();
  if (speeds.length < 4) return 0;
  speeds.sort((a, b) => a - b);
  return speeds[speeds.length >> 1];
}

/** 이동 속도(m/s) → 제자리 루프 클립 재생 배속. 이 범위를 벗어나는 배속은 동작이 망가져 보이므로 자른다 */
export function gaitPlayback(speed: number, clipGroundSpeed: number, lo = 0.35, hi = 2.2): number {
  if (!(clipGroundSpeed > 0)) return 1;
  const r = speed / clipGroundSpeed;
  return r < lo ? lo : r > hi ? hi : r;
}
